import React from "react";
import { useActiveIntegrations } from "src/scenes/dashboard/active-integrations-provider";
import NotIntegrated from "src/scenes/dashboard/not-integrated";
import IntegratedNoData from "src/scenes/dashboard/integrated-no-data";
import { Button, Stack } from "@mui/material";
import KnowledgebaseIconLink from "src/components/knowledgebase-icon-link";
import printDateRange from "src/components/print-daterange";
import InvalidIntegrationButton from "src/components/invalid-integration-button";
import styled from "styled-components";
import OrgDeploymentsConfigModal from "src/scenes/per-repo/org-deployments-config-modal";
import SettingsIcon from "@mui/icons-material/Settings";
import fetchStatus from "src/utils/api/fetch-status";
import IntegratedNoResponse from "src/scenes/dashboard/integrated-no-response";
import Template from "src/components/template/template";
import { getIsDashboardNotIntegrated } from "src/utils/is-not-integrated";
import { useTemplateViewEdit } from "src/components/providers/template-view-edit-provider";
import BackspaceIcon from "@mui/icons-material/Backspace";
import SaveIcon from "@mui/icons-material/Save";
import BorderClearIcon from "@mui/icons-material/BorderClear";
import TemplateAdd from "src/components/template/template-add";
import TemplateRemove from "src/components/template/template-remove";
import TemplateTitle from "src/components/template/template-title";
import TemplateItemEditDrawer from "src/components/template/template-item-edit-drawer";
import { Link as RouteLink } from "react-router-dom";
import ViewListIcon from "@mui/icons-material/ViewList";

const TemplateHelper = styled.p`
    font-size: 12px;
    font-style: normal;
    font-weight: 200;
    opacity: 0.7;
    margin: 0;
`;

const DashboardButton = styled(Button)`
    background-color: ${({ theme }) => theme.palette.background.default};
    font-size: 12px;
    margin-top: 13px;
    z-index: 10;
`;

const HelperMessage = "All comparative data are calculated weekly";

const PerRepoWorkAnalytics = ({ repos, paramRepos, paramGroups, isDemo }) => {
    if (!paramRepos && !paramGroups) return null;
    const [deploymentsConfigModal, setDeploymentsConfigModal] = React.useState(false);
    const [toolsScrapping, setToolsScrapping] = React.useState([]);
    const [templateNoData, setTemplateNoData] = React.useState({});
    const [earliestFirstScrapped, setEarliestFirstScrapped] = React.useState({});
    const [apiStatus, setApiStatus] = React.useState({});

    const { activeIntegrations, isIntegrated, isCentrallyManaged } = useActiveIntegrations();

    const { dashboard, saveChanges, discardChanges, isDirty, onEditMode, setOnEditMode } =
        useTemplateViewEdit();

    React.useEffect(() => {
        if (activeIntegrations.status !== "PENDING") {
            const filterIntegrationStatus = () => {
                const { integrations } = activeIntegrations.data || [];
                const scrappingIntegrations = integrations
                    .filter((integration) => integration.status === "scrapping")
                    .map((integration) => integration.name);

                return scrappingIntegrations;
            };
            setToolsScrapping(filterIntegrationStatus);
        }
    }, activeIntegrations.status);

    const notIntegrated = React.useMemo(
        () =>
            getIsDashboardNotIntegrated(dashboard, {
                github: isIntegrated.github || isCentrallyManaged.github,
                gitlab: isIntegrated.gitlab || isCentrallyManaged.gitlab,
                gitlabSelfManaged:
                    isIntegrated.gitlabSelfManaged || isCentrallyManaged.gitlabSelfManaged,
                slack: isIntegrated.slack || isCentrallyManaged.slack,
                google: isIntegrated.google || isCentrallyManaged.google,
                jira: isIntegrated.jira || isCentrallyManaged.jira,
                notion: isIntegrated.notion || isCentrallyManaged.notion,
                microsoft: isIntegrated.microsoft || isCentrallyManaged.microsoft,
            }),
        [dashboard && dashboard.templates, isIntegrated]
    );

    if (notIntegrated) {
        return <NotIntegrated name="work" />;
    }

    if (
        Object.values(templateNoData).length > 0 &&
        Object.values(templateNoData).length === dashboard?.templates?.length &&
        Object.values(templateNoData).filter((v) => v).length ===
            Object.values(templateNoData).length
    ) {
        return (
            <IntegratedNoData
                firstScrappedDate={Math.min(
                    ...Object.values(earliestFirstScrapped).map((d) => new Date(d).valueOf())
                )}
            />
        );
    }

    if (
        Object.values(apiStatus).length > 0 &&
        Object.values(apiStatus).length === dashboard?.templates?.length &&
        Object.values(apiStatus).filter((v) => v.status === fetchStatus.ERROR).length ===
            Object.values(apiStatus).length
    ) {
        return <IntegratedNoResponse />;
    }

    return (
        <Stack spacing={3} mt={2} mb={4}>
            {onEditMode ? (
                <Stack direction="row" style={{ width: "" }}>
                    <Button
                        startIcon={<BackspaceIcon />}
                        onClick={() => {
                            setOnEditMode(false);
                            discardChanges();
                        }}
                    >
                        {isDirty ? "Discard" : "Cancel"}
                    </Button>
                    <Button
                        disabled={!isDirty}
                        startIcon={<SaveIcon />}
                        onClick={() => {
                            setOnEditMode(false);
                            saveChanges({ users: "none" });
                        }}
                    >
                        Save
                    </Button>
                </Stack>
            ) : (
                <Button startIcon={<BorderClearIcon />} onClick={() => setOnEditMode(true)}>
                    Edit template
                </Button>
            )}
            {(dashboard?.templates || []).map((template, templateIndex) => {
                return (
                    <>
                        {onEditMode && <TemplateAdd templateIndex={templateIndex} />}
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{ width: "100%" }}
                        >
                            <Stack direction="row" alignItems="center">
                                <TemplateTitle
                                    text={template.title.value}
                                    templateIndex={templateIndex}
                                />
                                {template.knowledgeBaseName && (
                                    <KnowledgebaseIconLink name={template.knowledgeBaseName} />
                                )}
                                <TemplateHelper>{printDateRange()}</TemplateHelper>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                {onEditMode ? (
                                    <TemplateRemove templateIndex={templateIndex} />
                                ) : (
                                    <TemplateHelper>{HelperMessage}</TemplateHelper>
                                )}
                                {template.configurationAction &&
                                    template.configurationAction.type ===
                                        "deploymentConfiguration" && (
                                        <DashboardButton
                                            variant="outlined"
                                            color="secondary"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDeploymentsConfigModal(true);
                                            }}
                                            startIcon={<SettingsIcon />}
                                            style={{
                                                textDecoration: "auto",
                                                textAlign: "center",
                                                paddingRight: "12px",
                                            }}
                                        >
                                            Configuration
                                        </DashboardButton>
                                    )}
                                {template.configurationAction &&
                                    template.configurationAction.type ===
                                        "indexesConfiguration" && (
                                        <Button
                                            component={RouteLink}
                                            to="/indexes-settings"
                                            variant="contained"
                                            startIcon={<ViewListIcon />}
                                            style={{ width: "257px", fontSize: "12px" }}
                                        >
                                            Configure Indexes
                                        </Button>
                                    )}
                                <InvalidIntegrationButton
                                    dependencies={template.dependencies}
                                    isTeams={false}
                                />
                            </Stack>
                        </Stack>

                        <Template
                            viewType="repo"
                            onEditMode={onEditMode}
                            toolsScrapping={toolsScrapping}
                            template={template}
                            templateIndex={templateIndex}
                            defaultDateRange={dashboard.defaultDateRange}
                            repos={paramRepos}
                            groups={paramGroups}
                            setTemplateNoData={(noData) =>
                                setTemplateNoData((prevState) => ({
                                    ...prevState,
                                    [template.id]: noData,
                                }))
                            }
                            setEarliestFirstScrapped={(fsd) =>
                                setEarliestFirstScrapped((prevState) => ({
                                    ...prevState,
                                    [template.id]: fsd,
                                }))
                            }
                            setApiStatus={(status, errorCode) =>
                                setApiStatus((prevState) => ({
                                    ...prevState,
                                    [template.id]: { status, errorCode },
                                }))
                            }
                        />
                    </>
                );
            })}
            {onEditMode && <TemplateAdd templateIndex={dashboard?.templates?.length ?? 0} />}
            <OrgDeploymentsConfigModal
                open={deploymentsConfigModal}
                isDemo={isDemo}
                repos={repos}
                handleClose={() => setDeploymentsConfigModal(!deploymentsConfigModal)}
                fetchAction={undefined}
            />
            <TemplateItemEditDrawer />
        </Stack>
    );
};

export default PerRepoWorkAnalytics;
